// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/DataQueues/EventDialogServiceTimeSidebar/usePaymentLogic.js
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { API_URLS, getSelectedLanguage } from '../../../api/api.js';
import { languageManager } from '../../../utils/loadLanguage';
import bankInfo from '../../../data/bankInfo.json';

const usePaymentLogic = ({ cumulativePrice, handleSubmit, onHide, hideServiceTypeSidebar, onHideDialog, refreshParentData }) => {
  const dispatch = useDispatch();
  const [paymentMethodGroup1, setPaymentMethodGroup1] = useState('payCash');
  const [paymentMethodGroup2, setPaymentMethodGroup2] = useState('store');
  const [paymentPercentage, setPaymentPercentage] = useState('full');
  const [paymentAmount, setPaymentAmount] = useState(cumulativePrice);
  const [loading, setLoading] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({ type: '', details: {} });
  const [translations, setTranslations] = useState({});
  const [topSidebarVisible, setTopSidebarVisible] = useState(false);
  const sidebarContentRef = useRef(null);

  useEffect(() => {
    async function loadLang() {
      const lang = getSelectedLanguage();
      await languageManager.loadLanguage(lang);
      setTranslations(languageManager.translations);
    }
    loadLang();
  }, []);

  useEffect(() => {
    setPaymentAmount(cumulativePrice);
    scrollToBottom();
  }, [cumulativePrice]);

  const simulateAvailableTimes = (times) => {
    const simulatedTimes = times.map((slot) => {
      const isAvailable = slot.statusHH === "Available";
      return { ...slot, statusHH: isAvailable ? "Available" : "Unavailable", disabled: isAvailable };
    });
    return simulatedTimes;
  };

  const handlePaymentPercentageChange = (percentage) => {
    setPaymentPercentage(percentage);
    setPaymentAmount(percentage === 'half' ? cumulativePrice / 2 : (percentage === 'full' ? cumulativePrice : 0));
  };

  const handlePaymentMethodChangeGroup1 = (method) => {
    setPaymentMethodGroup1(method);
    setPaymentAmount(paymentPercentage === 'half' ? cumulativePrice / 2 : cumulativePrice);
    if (['payCash', 'qrCode'].includes(method)) loadPaymentDetails(method);
  };

  const handlePaymentMethodChangeGroup2 = (method) => {
    setPaymentMethodGroup2(method);
  };

  const loadPaymentDetails = (method) => {
    const details = method === 'payCash' ? bankInfo.payCash : bankInfo.qrCode;
    setPaymentDetails({ type: method, details });
  };

  const handlePaymentAmountChange = (value) => setPaymentAmount(value.replace(/\D/g, ''));
  const handleInputFocus = () => setPaymentAmount('');
  const handleInputBlur = (e) => { if (!e.target.value) setPaymentAmount(cumulativePrice); };

  const generateReferenceCode = () => {
    return [...Array(32)].map(() => Math.random().toString(36)[2]).join('');
  };

  const preparePaymentData = (referenceNumber, bookingID, customerID, employeeID, amount, remainingAmount, notes, currency) => {
    return [{
      ReferenceNumber: referenceNumber,
      TransactionDate: new Date().toISOString(),
      BookingID: bookingID,
      ExpenseTypeID: 9,
      CustomerID: customerID,
      EmployeeID: employeeID,
      Amount: amount,
      Currency: currency,
      Remaining: remainingAmount,
      PaymentMethod: "5",
      PaymentStatus: "7",
      Status: "4",
      Notes: notes
    }];
  };

  const fetchLatestBookingData = async (currency) => {
    try {
      const response = await axios.get(API_URLS.EVENTS, {
        params: {
          date_from: new Date().toISOString().split('T')[0],
          date_to: new Date().toISOString().split('T')[0]
        },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const bookingData = response.data;
      if (Array.isArray(bookingData) && bookingData.length > 0) {
        const latestBooking = bookingData[0];
        const referenceCode = generateReferenceCode();
        const notes = `${paymentMethodGroup2 === 'store' ? 'In-Store' : 'Online'}, ${paymentMethodGroup1 === 'payCash' ? 'Pay Cash' : paymentMethodGroup1 === 'visa' ? 'Visa' : 'QR-Code'}`;
        const remainingAmount = latestBooking.PriceValue - latestBooking.AmountPaid;

        const paymentData = preparePaymentData(
          referenceCode,
          latestBooking.BookingID,
          latestBooking.CustomerID,
          latestBooking.EmployeeID,
          latestBooking.PriceValue,
          remainingAmount,
          notes,
          currency
        );

        await sendPaymentData(paymentData);
        await sendEmailNotification(latestBooking.BookingID, latestBooking.CustomerEmail);
        await sendSmsNotification(latestBooking.BookingID, latestBooking.CustomerTelephone, currency === "THB" ? "PURPLE LION PACIFIC" : "RAMPAGESOFT");
      }
    } catch (error) {
      console.error('Error fetching latest booking data:', error);
    }
  };

  const sendEmailNotification = async (bookingID, customerEmail) => {
    try {
      const emailData = { order_id: bookingID, email: customerEmail };
      await axios.post(API_URLS.SEND_EMAIL, emailData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error sending email notification:', error);
    }
  };

  const sendSmsNotification = async (bookingID, customerPhone, sender) => {
    try {
      customerPhone = customerPhone.replace(/\s+/g, '');
      if (!customerPhone.startsWith('+')) customerPhone = `+66${customerPhone}`;
      const smsData = {
        sms_recipients: [customerPhone],
        sms_text: `Thank you for your payment! OrderID ${bookingID} isaanspa`,
        sms_type: "SMS_WORLD",
        sms_sender: sender
      };
      await axios.post(API_URLS.SEND_SMS, smsData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error sending SMS notification:', error);
    }
  };

  const sendPaymentData = async (paymentData) => {
    try {
      await axios.post(API_URLS.CREATE_PAYMENT, paymentData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error sending payment data:', error);
    }
  };

  const handleSubmitClick = async (currency, setShouldFetch) => {
    setLoading(true);
    await handleSubmit(paymentAmount);
    setLoading(false);
    onHide();
    hideServiceTypeSidebar();
    setTopSidebarVisible(false);
    setShouldFetch(true);
    if (typeof onHideDialog === 'function') onHideDialog();
    if (typeof refreshParentData === 'function') refreshParentData();
    dispatch({ type: 'SET_HANDLE_SUBMIT_CLICK_STATUS', payload: 1 });
    await fetchLatestBookingData(currency);
  };

  const scrollToBottom = () => {
    if (sidebarContentRef.current) sidebarContentRef.current.scrollTop = sidebarContentRef.current.scrollHeight;
  };

  return {
    paymentMethodGroup1,
    paymentMethodGroup2,
    paymentPercentage,
    paymentAmount,
    loading,
    paymentDetails,
    translations,
    topSidebarVisible,
    sidebarContentRef,
    setPaymentMethodGroup1,
    setTopSidebarVisible,
    setPaymentAmount,
    handlePaymentPercentageChange,
    handlePaymentMethodChangeGroup1,
    handlePaymentMethodChangeGroup2,
    handlePaymentAmountChange,
    handleInputFocus,
    handleInputBlur,
    handleSubmitClick,
    simulateAvailableTimes, // Added to returned object
  };
};

export default usePaymentLogic;